import {
  ArrowLeft,
  Save,
  Play,
  Webhook,
  MousePointerClick,
  CalendarClock,
  Zap,
  History,
  Loader2,
  Eye,
  LayoutDashboard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import type TriggerDefinition from "../../models/TriggerDefinition";
import type { TriggerType } from "../../models/TriggerDefinition";

const TRIGGER_OPTIONS: {
  type: TriggerType;
  icon: typeof Webhook;
  label: string;
  disabled?: boolean;
}[] = [
  { type: "Manual", icon: MousePointerClick, label: "Manual" },
  { type: "Webhook", icon: Webhook, label: "Webhook" },
  { type: "Scheduled", icon: CalendarClock, label: "Programado", disabled: true },
  { type: "Invoke", icon: Zap, label: "Invocación", disabled: true },
];

const SECONDARY_BUTTON =
  "flex items-center gap-2 h-9 px-4 rounded-xl border border-black/5 bg-white/60 text-sm font-semibold text-foreground/70 hover:bg-white hover:text-accent hover:shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

interface CanvasToolbarProps {
  name: string;
  trigger: TriggerDefinition;
  onNameChange: (name: string) => void;
  onTriggerChange: (trigger: TriggerDefinition) => void;
  onSave: () => void;
  onRun: () => void;
  onShowHistory?: () => void;
  onOpenDashboard?: () => void;
  /** Tooltip del botón de dashboard: nombra el workspace del que cuelga. */
  dashboardLabel?: string;
  saving: boolean;
  running: boolean;
  /**
   * La automatización llegó por una concesión: se puede abrir y ejecutar, pero publicar una
   * versión nueva lo rechaza el backend con 403. Oculta Guardar y bloquea nombre y trigger.
   */
  readOnly?: boolean;
}

export default function CanvasToolbar({
  name,
  trigger,
  onNameChange,
  onTriggerChange,
  onSave,
  onRun,
  onShowHistory,
  onOpenDashboard,
  dashboardLabel,
  saving,
  running,
  readOnly = false,
}: CanvasToolbarProps) {
  const navigate = useNavigate();

  const handleTriggerTypeChange = (type: TriggerType) => {
    onTriggerChange({
      type,
      requiresIdempotencyKey: type === "Webhook",
    });
  };

  return (
    <header className="flex items-center gap-3 px-4 py-2.5 border-b border-black/5 bg-white/70 backdrop-blur-md">
      <button
        onClick={() => navigate("/admin/automations")}
        className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-black/5 bg-white/60 text-foreground/60 hover:bg-white hover:text-accent hover:shadow-sm transition-all cursor-pointer"
        title="Volver a automatizaciones"
      >
        <ArrowLeft className="size-4" />
      </button>

      <input
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        readOnly={readOnly}
        placeholder="Nombre de la automatización"
        className="flex-1 max-w-md px-3 py-1.5 rounded-xl bg-transparent text-lg font-bold text-foreground border border-transparent hover:bg-black/[0.03] focus:bg-white/80 focus:border-accent/30 focus:outline-none transition-all placeholder:text-foreground/30 placeholder:font-normal read-only:hover:bg-transparent read-only:cursor-default"
      />

      {readOnly && (
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold text-amber-600"
          title="Te compartieron esta automatización: podés verla y ejecutarla, pero no modificarla"
        >
          <Eye className="size-3.5" />
          SOLO LECTURA
        </span>
      )}

      {/* Trigger segmented control */}
      <div className="ml-auto flex items-center gap-0.5 p-1 rounded-xl bg-black/5 border border-black/5 shrink-0">
        {TRIGGER_OPTIONS.map(({ type, icon: Icon, label, disabled }) => {
          const active = trigger.type === type;
          return (
            <button
              key={type}
              onClick={() => !disabled && !readOnly && handleTriggerTypeChange(type)}
              disabled={disabled || readOnly}
              title={disabled ? `${label} (próximamente)` : `Trigger ${label}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                active
                  ? "bg-white text-accent shadow-sm"
                  : disabled
                    ? "text-foreground/25 cursor-not-allowed"
                    : "text-foreground/50 hover:text-foreground/80 cursor-pointer"
              }`}
            >
              <Icon className="size-3.5" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {onOpenDashboard && (
          <button onClick={onOpenDashboard} className={SECONDARY_BUTTON} title={dashboardLabel ?? "Dashboard del workspace"}>
            <LayoutDashboard className="size-4" />
            Dashboard
          </button>
        )}
        {onShowHistory && (
          <button onClick={onShowHistory} className={SECONDARY_BUTTON} title="Historial de ejecuciones">
            <History className="size-4" />
            Historial
          </button>
        )}
        {!readOnly && (
          <button onClick={onSave} disabled={saving} className={SECONDARY_BUTTON}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Guardar
          </button>
        )}
        <button
          onClick={onRun}
          disabled={running}
          className="flex items-center gap-2 h-9 px-5 rounded-xl bg-accent text-white text-sm font-semibold shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-accent/35 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          Ejecutar
        </button>
      </div>
    </header>
  );
}
